import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { IremboDataTableComponent } from '../irembo-data-table/irembo-data-table.component';
import {
  ItemSelectionConfig,
  SelectionValidationErrors,
} from '../../models/irembo-data-table.model';

@Component({
  selector: 'irembogov-item-selection-table',
  templateUrl: './irembo-item-selection-table.component.html',
  styleUrls: ['./irembo-item-selection-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IremboItemSelectionTableComponent
  extends IremboDataTableComponent
  implements OnInit, OnChanges
{
  @Input() selectionConfig!: ItemSelectionConfig;
  @Input() targetItems: any[] = []; // Reference to target array for add mode
  @Input() debugMode = false; // Debug mode flag

  @Output() itemAdded = new EventEmitter<any>();
  @Output() itemRemoved = new EventEmitter<any>();
  @Output() validationError = new EventEmitter<SelectionValidationErrors>();

  validationErrors: SelectionValidationErrors = {};

  override validate(): ValidationErrors | null {
    const validationErrors: ValidationErrors = {};
    const config = this.selectionConfig;

    if (!config) return null;

    const itemCount =
      config.mode === 'add' ? this.targetItems.length : this.data.length;

    // Use minSelection/maxSelection from base class
    if (this.minSelection !== null && itemCount < this.minSelection) {
      validationErrors['minSelection'] = true;
    }

    if (this.maxSelection !== null && itemCount > this.maxSelection) {
      validationErrors['maxSelection'] = true;
    }

    return Object.keys(validationErrors).length ? validationErrors : null;
  }

  ngOnInit(): void {
    this.debugLog('ngOnInit', 'Component initializing', {
      selectionConfig: this.selectionConfig,
      targetItems: this.targetItems,
      data: this.data,
    });

    this.validateConfig();
    this.syncValidationConstraints();
    this.updateValidation();

    this.debugLog('ngOnInit', 'Component initialized');
  }

  override ngOnChanges(changes: SimpleChanges): void {
    this.debugLog('ngOnChanges', 'Component inputs changed', {
      changes: {
        selectionConfig: changes['selectionConfig']
          ? {
              previousValue: changes['selectionConfig'].previousValue,
              currentValue: changes['selectionConfig'].currentValue,
              firstChange: changes['selectionConfig'].firstChange,
            }
          : undefined,
        targetItems: changes['targetItems']
          ? {
              previousValue: changes['targetItems'].previousValue,
              currentValue: changes['targetItems'].currentValue,
              firstChange: changes['targetItems'].firstChange,
            }
          : undefined,
        data: changes['data']
          ? {
              previousValue: changes['data'].previousValue,
              currentValue: changes['data'].currentValue,
              firstChange: changes['data'].firstChange,
            }
          : undefined,
      },
      currentData: this.data,
      currentTargetItems: this.targetItems,
    });

    super.ngOnChanges(changes);

    if (changes['selectionConfig']) {
      this.debugLog(
        'ngOnChanges',
        'Selection config changed - syncing constraints'
      );
      this.syncValidationConstraints();
    }

    if (
      changes['targetItems'] ||
      changes['data'] ||
      changes['selectionConfig']
    ) {
      this.debugLog(
        'ngOnChanges',
        'Data/config changed - updating validation',
        {
          targetItemsLength: this.targetItems?.length,
          dataLength: this.data?.length,
        }
      );

      this.updateValidation();
      // Trigger validation recheck
      if (this['_onValidatorChange']) {
        this['_onValidatorChange']();
      }
    }
  }

  private syncValidationConstraints(): void {
    if (!this.selectionConfig) return;

    // Map selectionConfig constraints to base class properties
    this.minSelection = this.selectionConfig.minItems ?? null;
    this.maxSelection = this.selectionConfig.maxItems ?? null;
  }

  private validateConfig(): void {
    if (!this.selectionConfig) {
      throw new Error(
        'selectionConfig is required for IremboItemSelectionTableComponent'
      );
    }

    if (!['add', 'remove'].includes(this.selectionConfig.mode)) {
      throw new Error('selectionConfig.mode must be "add" or "remove"');
    }

    if (
      this.selectionConfig.mode === 'add' &&
      !this.selectionConfig.targetField
    ) {
      console.warn('targetField recommended for add mode to track selections');
    }
  }

  handleItemAction(item: any): void {
    this.debugLog('handleItemAction', 'Item action triggered', {
      item: item,
      mode: this.selectionConfig.mode,
      canPerform: this.canPerformAction(item),
      currentDataLength: this.data?.length,
      currentTargetItemsLength: this.targetItems?.length,
    });

    if (!this.canPerformAction(item)) {
      this.debugLog('handleItemAction', 'Action not allowed', {
        item: item,
      });
      return;
    }

    if (this.selectionConfig.mode === 'add') {
      this.debugLog('handleItemAction', 'Adding item');
      this.addItem(item);
    } else if (this.selectionConfig.mode === 'remove') {
      this.debugLog('handleItemAction', 'Removing item');
      this.removeItem(item);
    }

    this.updateValidation();
    // Trigger validation recheck
    if (this['_onValidatorChange']) {
      this['_onValidatorChange']();
    }

    this.debugLog('handleItemAction', 'Action completed', {
      newDataLength: this.data?.length,
      newTargetItemsLength: this.targetItems?.length,
    });
  }

  private addItem(item: any): void {
    if (this.selectionConfig.preventDuplicates !== false) {
      if (this.isItemInTarget(item)) {
        return;
      }
    }

    this.targetItems.push(item);
    this.itemAdded.emit(item);
  }

  private removeItem(item: any): void {
    const index = this.data.indexOf(item);
    if (index > -1) {
      this.data.splice(index, 1);
      this.itemRemoved.emit(item);

      // Update pagination if needed
      if (this.data.length === 0 && this.currentPage > 1) {
        this.currentPage--;
      }
    }
  }

  canPerformAction(item: any): boolean {
    const config = this.selectionConfig;

    if (config.mode === 'add') {
      // Check max items
      if (
        config.maxItems !== undefined &&
        this.targetItems.length >= config.maxItems
      ) {
        return false;
      }

      // Check duplicates
      if (config.preventDuplicates !== false && this.isItemInTarget(item)) {
        return false;
      }
    }
    // Remove mode: Always allow removal
    // Validation will show error if min items constraint is violated

    return true;
  }

  private updateValidation(): void {
    this.validationErrors = {};
    const config = this.selectionConfig;

    if (!config) return;

    const itemCount =
      config.mode === 'add' ? this.targetItems.length : this.data.length;

    // Use minSelection/maxSelection (mapped from minItems/maxItems)
    if (this.minSelection !== null && itemCount < this.minSelection) {
      this.validationErrors.minSelection = true;
    }

    if (this.maxSelection !== null && itemCount > this.maxSelection) {
      this.validationErrors.maxSelection = true;
    }

    // Always emit validation errors (including empty object to clear errors)
    this.validationError.emit(this.validationErrors);
  }

  isItemInTarget(item: any): boolean {
    if (!this.targetItems) return false;

    return this.targetItems.some(
      targetItem => JSON.stringify(targetItem) === JSON.stringify(item)
    );
  }

  getItemIcon(item: any): string {
    const config = this.selectionConfig;

    if (config.mode === 'add') {
      if (this.isItemInTarget(item)) {
        // Item is selected - show selected icon
        return (
          config.selectedIcon ||
          config.removeIcon ||
          config.actionIcon ||
          'icon-check'
        );
      }
      // Item is available - show available icon
      return config.availableIcon || config.actionIcon || 'icon-plus';
    }

    // Remove mode - show remove icon
    return config.removeIcon || config.actionIcon || 'icon-trash';
  }

  getActionLabel(): string {
    if (this.selectionConfig.actionLabel) {
      return this.selectionConfig.actionLabel;
    }

    if (this.selectionConfig.mode === 'add') return 'Add';
    if (this.selectionConfig.mode === 'remove') return 'Remove';

    return 'Action';
  }

  shouldShowIcon(): boolean {
    const displayMode = this.selectionConfig.displayMode || 'icon-text';
    return displayMode === 'icon-only' || displayMode === 'icon-text';
  }

  shouldShowLabel(): boolean {
    const displayMode = this.selectionConfig.displayMode || 'icon-text';
    return displayMode === 'text-only' || displayMode === 'icon-text';
  }

  isIconButton(): boolean {
    return this.selectionConfig.buttonStyle === 'icon-button';
  }

  getButtonClass(): string {
    const config = this.selectionConfig;

    if (config.buttonStyle === 'icon-button') {
      // Icon-only button without outline
      return 'btn-link p-0 border-0';
    }

    // Default button with outline
    if (config.mode === 'add') {
      return 'btn-sm btn-primary';
    }

    if (config.mode === 'remove') {
      return 'btn-sm btn-danger';
    }

    return 'btn-sm';
  }

  /**
   * Debug logging utility - only logs when debugMode is enabled
   */
  private debugLog(method: string, message: string, data?: any): void {
    if (!this.debugMode) return;

    const logData = {
      timestamp: new Date().toISOString(),
      component: 'IremboItemSelectionTableComponent',
      method,
      message,
      changeDetectionStrategy: 'OnPush',
      ...(data && { data }),
    };

    console.log('[customserviceviewer:item-selection-table]', logData);
  }
}
